/**
 * router.tsx — a tiny hash-based router. Hash routes survive deep links with no
 * server fallback (the server only serves "/"). Patterns use `:param` segments.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties, MouseEvent, ReactNode } from "react";

export interface RouteMatch {
  path: string;
  params: Readonly<Record<string, string>>;
}

export interface RouteDef {
  id: string;
  pattern: string;
  render: (m: RouteMatch) => ReactNode;
}

function parseHash(): string {
  const raw = (typeof window !== "undefined" ? window.location.hash.slice(1) : "") || "/";
  return raw.startsWith("/") ? raw : "/" + raw;
}

function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const pp = pattern.split("/").filter(Boolean);
  const tp = path.split("/").filter(Boolean);
  if (pp.length !== tp.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < pp.length; i++) {
    const p = pp[i]!;
    const t = tp[i]!;
    if (p.startsWith(":")) params[p.slice(1)] = decodeURIComponent(t);
    else if (p !== t) return null;
  }
  return params;
}

export function navigate(path: string): void {
  const next = path.startsWith("#") ? path.slice(1) : path;
  if (window.location.hash.slice(1) === next) return;
  window.location.hash = next;
}

export function useRoute(): string {
  const [path, setPath] = useState(parseHash);
  useEffect(() => {
    const onChange = (): void => setPath(parseHash());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return path;
}

export function RouterView({ routes, notFound }: { routes: readonly RouteDef[]; notFound: (p: string) => ReactNode }): JSX.Element {
  const path = useRoute();
  const matched = useMemo(() => {
    for (const r of routes) {
      const params = matchRoute(r.pattern, path);
      if (params) return { route: r, params };
    }
    return null;
  }, [routes, path]);
  if (!matched) return <>{notFound(path)}</>;
  return <>{matched.route.render({ path, params: matched.params })}</>;
}

export function Link({ to, className, style, children }: { to: string; className?: string; style?: CSSProperties; children: ReactNode }): JSX.Element {
  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      navigate(to);
    },
    [to],
  );
  return (
    <a href={`#${to}`} className={className} style={style} onClick={onClick}>
      {children}
    </a>
  );
}
